import { Fragment, useCallback, useMemo, useState } from 'react';

import { useAuth } from '../../api.ts';
import { useMatrixCellScale } from '../../matrix-cell-scale.ts';
import {
  FloatingToolbar,
  GroupSkillsIcon,
  ShowLibrariesIcon,
  UltraCompactIcon,
  ToolbarMultiSelect,
  ToolbarSelect,
  ToolbarSeparator,
  ToolbarToggle,
} from '../components/FloatingToolbar.tsx';
import { MatrixCell } from '../components/MatrixCell.tsx';
import { MatrixScrollBox } from '../components/MatrixScrollBox.tsx';
import { EmptyView, ErrorView, SignInView, Skeleton } from '../components/Skeleton.tsx';
import type { TooltipContent } from '../components/Tooltip.tsx';
import {
  ColumnHeader,
  ConnectorCell,
  GroupedCompLabel,
  PropIndicator,
  TruncatedLabel,
  nestVariant,
} from '../components/TruncatedLabel.tsx';
import {
  RATING_LABELS,
  TALENT_LEVELS,
  buildDerivedContextFunctionGate,
  buildRatingIndex,
  buildSkillFunctions,
  canUltraCompact,
  compRowHeight,
  coverageForCell,
  indexSkills,
  isCompactCatalog,
  isInstitutional,
  levelClass,
  NO_EXPANDED,
  remapSkillFunctionsToParents,
  rollupColumns,
  ultraCompactRowHeight,
} from '../data-views.ts';
import type {
  BusinessFunction,
  DerivedContextFunctionGate,
  DerivedContext,
  Engineer,
  RatingValue,
  RollupColumn,
  Skill,
} from '../data-views.ts';
import { sortByClientDistance } from '../derived-context-order.ts';
import { useResilienceView } from '../hooks/useViews.ts';
import { makeMergedCoverageCell } from '../matrix-cells-views.ts';
import { useTeamFilter } from '../team-filter.ts';
import { useTechFilter } from '../tech-filter.ts';

const SKELETON_SUBTITLE = 'All engineers across the platform.';

export function ResilienceView() {
  const auth = useAuth();
  if (auth.status === 'loading') {
    return <Skeleton title='Resilience' subtitle={SKELETON_SUBTITLE} rows={8} />;
  }
  if (auth.status === 'signed-out') {
    return <SignInView title='Resilience' />;
  }
  return <ResilienceLoader />;
}

function ResilienceLoader() {
  const { data: view, isLoading, error } = useResilienceView();
  if (error) {
    return <ErrorView title='Resilience' error={error} />;
  }
  if (isLoading || !view) {
    return (
      <Skeleton title='Resilience' subtitle={SKELETON_SUBTITLE} rows={8} />
    );
  }
  if (view.derivedContexts.length === 0 || view.engineers.length === 0) {
    return <EmptyView title='Resilience' subtitle={SKELETON_SUBTITLE} />;
  }
  return (
    <ResilienceBody
      derivedContexts={view.derivedContexts}
      functions={view.business_functions}
      allEngineers={view.engineers}
      ratings={view.ratings}
    />
  );
}

function ResilienceBody({
  derivedContexts,
  functions,
  allEngineers,
  ratings,
}: {
  derivedContexts: DerivedContext[];
  functions: BusinessFunction[];
  allEngineers: Engineer[];
  ratings: import('../data-views.ts').Rating[];
}) {
  const [talentLevel, setTalentLevel] = useState<RatingValue>(2);
  // Default to grouped: one row per derivedContext keeps the matrix compact
  // and matches Timeline / Roadmap. Toolbar toggle expands to per-skill.
  const [grouped, setGrouped] = useState(true);
  // Per-derivedContext skill expand/collapse — click a derivedContext's connector line
  // to list its skills (or fold them back), the same affordance the /setup
  // editor uses. `grouped` is the baseline; `expandOverrides` holds the ids
  // that differ from it, so "Group Skills" reads as collapse/expand-all while
  // individual connectors fine-tune single derivedContexts.
  const [expandOverrides, setExpandOverrides] = useState<Set<string>>(new Set());
  const isCompExpanded = useCallback(
    (id: string) => (expandOverrides.has(id) ? grouped : !grouped),
    [expandOverrides, grouped],
  );
  const toggleCompExpanded = useCallback((id: string) => {
    setExpandOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const setAllGrouped = useCallback((g: boolean) => {
    setGrouped(g);
    setExpandOverrides(new Set());
  }, []);
  const techFilter = useTechFilter();
  const teamFilter = useTeamFilter();
  // Ultra-compact rows — off by default, and only offered at all on a catalog
  // big enough to need it (see `canUltraCompact`).
  const [ultra, setUltra] = useState(false);
  const ultraAvailable = canUltraCompact(derivedContexts);
  // `ultra` is the latched preference; `ultraOn` is whether it applies to the
  // catalog on screen. Same split as Timeline, so a latched preference can
  // never outlive the control that clears it.
  const ultraOn = ultra && ultraAvailable;

  // Narrow the engineer set to the selected team before any coverage math.
  const team = useMemo(() => teamFilter.filter(allEngineers), [teamFilter, allEngineers]);

  const skillsById = useMemo(() => indexSkills(derivedContexts), [derivedContexts]);
  const skillFunctions = useMemo(
    () => buildSkillFunctions(derivedContexts),
    [derivedContexts]
  );
  const ratingIndex = useMemo(
    () => buildRatingIndex(skillsById, team, ratings),
    [skillsById, team, ratings]
  );

  const visibleComps = useMemo(() => {
    // Auto-derived client-distance order (client-facing first) is the default
    // row order; rank on the full skill set so it's stable across tech filters.
    return sortByClientDistance(derivedContexts)
      .map(comp => ({
        ...comp,
        skills: comp.skills.filter(techFilter.visible),
        // Pre-filter length, kept for ultra-compact sizing. Holding it here is
        // what makes the ultra layout invariant under "Show Libraries" — see
        // `ultraCompactRowHeight`.
        allSkillCount: comp.skills.length,
      }))
      .filter(comp => comp.skills.length > 0);
  }, [derivedContexts, techFilter]);

  // The column axis: the promoted, top-level functions, each parent collapsed
  // over its children. `skillFunctionsGrouped` remaps children onto their
  // parent so a group column's cells aggregate across the whole group.
  const columns = useMemo<RollupColumn[]>(
    () => rollupColumns(functions, NO_EXPANDED),
    [functions]
  );
  const skillFunctionsGrouped = useMemo(
    () => remapSkillFunctionsToParents(skillFunctions, functions),
    [skillFunctions, functions]
  );
  // The shared platform-map cell gate: a (derivedContext × function) cell exists
  // only where the derivedContext itself serves the function (its own edges), never
  // via the per-skill union — so shared/observed skills can't manufacture
  // phantoms. Coverage inside a served cell still counts those skills.
  const cfGate = useMemo(
    () => buildDerivedContextFunctionGate(derivedContexts, functions),
    [derivedContexts, functions]
  );

  const gridStyle = useMemo(() => {
    const base: Record<string, string> = {
      gridTemplateColumns: `130px 42px 6px 140px repeat(${columns.length}, minmax(72px, 1fr))`,
    };
    // One track per rendered row (header + derivedContexts): a collapsed
    // derivedContext is a single band sized to its skill count (so high-surface
    // comps read heavier); an expanded one lists a row per skill. Explicit
    // tracks let collapsed and expanded derivedContexts coexist in one grid.
    const compact = isCompactCatalog(derivedContexts);
    const rows = ['auto'];
    for (const c of visibleComps) {
      if (isCompExpanded(c.id)) {
        // Expanding is an explicit request for detail, so a per-skill row keeps
        // its readable height even in ultra-compact.
        for (let i = 0; i < c.skills.length; i += 1) rows.push('minmax(40px, auto)');
      } else if (ultraOn) {
        rows.push(ultraCompactRowHeight(c.allSkillCount) + 'px');
      } else {
        rows.push(compRowHeight(c.skills.length, compact) + 'px');
      }
    }
    base.gridTemplateRows = rows.join(' ');
    return base;
  }, [columns.length, visibleComps, derivedContexts, isCompExpanded, ultraOn]);

  const levelLabel =
    TALENT_LEVELS.find(l => l.key === talentLevel)?.label ?? '';

  // Cumulative left offsets for the four frozen label columns
  // (130/42/6/140 + the 6px grid gap) so they pin left when the business
  // functions overflow horizontally.
  const freezeLefts = [0, 136, 184, 196];

  return (
    <section>
      <h1 className='snap-h1'>Resilience</h1>
      <p className='snap-subtitle'>
        All <span className='snap-subtitle__dyn snap-subtitle__dyn--plain'>{levelLabel}</span>{' '}
        engineers across the platform.
      </p>

      <FloatingToolbar>
        <ToolbarSelect
          label='Filter by talent level'
          value={String(talentLevel)}
          options={TALENT_LEVELS.map(l => ({
            value: String(l.key),
            label: l.label,
          }))}
          onChange={v => setTalentLevel(Number(v) as RatingValue)}
        />
        {teamFilter.show ? (
          <ToolbarMultiSelect
            label='Filter by team'
            selected={teamFilter.selected}
            options={teamFilter.options}
            onChange={teamFilter.setSelected}
            allLabel='All teams'
          />
        ) : null}
        <ToolbarSeparator />
        <ToolbarToggle
          label='Group Skills'
          pressed={grouped}
          onToggle={() => setAllGrouped(!grouped)}
        >
          <GroupSkillsIcon />
        </ToolbarToggle>
        <ToolbarToggle
          label='Show Libraries'
          pressed={techFilter.showLibraries}
          onToggle={() => techFilter.setShowLibraries(!techFilter.showLibraries)}
        >
          <ShowLibrariesIcon />
        </ToolbarToggle>
        {ultraAvailable ? (
          <ToolbarToggle
            label={
              ultra
                ? 'Ultra-compact rows: on'
                : 'Ultra-compact rows — fit a large catalog on one screen'
            }
            pressed={ultra}
            onToggle={() => setUltra(v => !v)}
          >
            <UltraCompactIcon />
          </ToolbarToggle>
        ) : null}
        {/* The facet-grounding switch and the reliance-weight cell-scale debug
            toggle used to sit here. Both are internal instrumentation, not user
            surface, so they are no longer exposed. The stores behind them
            (`matrix-grounding`, `matrix-cell-scale`) are intact and still read
            by the view + query key — grounding stays at its `'glob'` default and
            cell scaling stays off unless the localStorage key is set by hand. */}
      </FloatingToolbar>

      <MatrixScrollBox>
        <div
          className={`snap-grid${ultraOn ? ' snap-grid--ultra' : ''}`}
          style={gridStyle}
        >
          <div className='snap-grid__freeze-corner is-frozen' style={{ left: freezeLefts[0] }} />
          <div className='snap-grid__freeze-corner is-frozen' style={{ left: freezeLefts[1] }} />
          <div className='snap-grid__freeze-corner is-frozen' style={{ left: freezeLefts[2] }} />
          <div
            className='snap-grid__freeze-corner is-frozen is-frozen-edge'
            style={{ left: freezeLefts[3] }}
          />
          {columns.map(col => (
            <ColumnHeader
              key={col.fn.id}
              display={col.fn.display}
              subtitle={col.fn.subtitle}
            />
          ))}

          {visibleComps.map(comp =>
            isCompExpanded(comp.id) ? (
              <FlatGroup
                key={comp.id}
                comp={comp}
                columns={columns}
                team={team}
                threshold={talentLevel}
                levelLabel={levelLabel}
                ratingIndex={ratingIndex}
                skillFunctions={skillFunctions}
                skillFunctionsGrouped={skillFunctionsGrouped}
                cfGate={cfGate}
                freezeLefts={freezeLefts}
                onToggleExpand={() => toggleCompExpanded(comp.id)}
              />
            ) : (
              <GroupedRow
                key={comp.id}
                comp={comp}
                columns={columns}
                team={team}
                threshold={talentLevel}
                levelLabel={levelLabel}
                ratingIndex={ratingIndex}
                skillFunctions={skillFunctions}
                skillFunctionsGrouped={skillFunctionsGrouped}
                cfGate={cfGate}
                freezeLefts={freezeLefts}
                ultra={ultraOn}
                onToggleExpand={() => toggleCompExpanded(comp.id)}
              />
            ),
          )}
        </div>
      </MatrixScrollBox>
    </section>
  );
}

interface RowProps {
  comp: DerivedContext;
  columns: RollupColumn[];
  team: Engineer[];
  threshold: RatingValue;
  levelLabel: string;
  ratingIndex: ReturnType<typeof buildRatingIndex>;
  /** Flat skill→function map — used for plain / revealed-child columns. */
  skillFunctions: ReturnType<typeof buildSkillFunctions>;
  /** Children remapped onto their parent — used for `group` columns so the
   *  cell aggregates coverage across the whole group. */
  skillFunctionsGrouped: ReturnType<typeof buildSkillFunctions>;
  /** Shared platform-map cell gate — a cell exists only where the derivedContext
   *  itself serves the function (blocks shared-skill phantoms). */
  cfGate: DerivedContextFunctionGate;
  freezeLefts: number[];
  /** Ultra-compact rows: drops the "N proprietary" line (the green dot stays).
   *  Only ever true on the collapsed `GroupedRow`. */
  ultra?: boolean;
  /** Toggle this derivedContext between the collapsed band and its expanded
   *  skill list — wired to the connector line. */
  onToggleExpand: () => void;
}

/** Pick the adjacency map a column's cells should use. */
function mapForColumn(
  col: RollupColumn,
  flat: ReturnType<typeof buildSkillFunctions>,
  grouped: ReturnType<typeof buildSkillFunctions>,
): ReturnType<typeof buildSkillFunctions> {
  return col.kind === 'group' ? grouped : flat;
}

/**
 * Mark HOW a context reaches the business function in this cell.
 *
 *   direct         — same-file overlap. Proven. No extra class; the cell's level
 *                    colour already says everything.
 *   dependency     — the BF's footprint imports this context's. Proven, via the
 *                    import graph.
 *   infrastructure — the context's own authored edge. The MODEL said so.
 *
 * THE TWO INDIRECT KINDS GET SEPARATE CLASSES even though they are painted the
 * same today. They previously shared one class, which meant the matrix could not
 * distinguish "proven through the import graph" from "the model asserted it" —
 * different confidence, shown identically, with no way to tell from the DOM
 * either. Emitting them apart costs nothing and makes the distinction available
 * the moment we want to draw it.
 *
 * `function_kinds` is absent for stored-edge orgs, so a missing entry reads as
 * `direct`.
 */
function cellKindClass(comp: DerivedContext, fnId: string): string {
  const kind = comp.function_kinds?.[fnId];
  if (kind === 'dependency') return 'is-dependency';
  if (kind === 'infrastructure') return 'is-infrastructure';
  return '';
}

/**
 * A cell's share of its DERIVED_CONTEXT's total evidence, 0..1 — so scanning a row
 * answers "what does this context mostly serve?".
 *
 * Row-relative rather than absolute because absolute mass is dominated by how
 * large a derivedContext is, which would make every cell of a big derivedContext look
 * strong and every cell of a small one look weak — the wrong comparison for a
 * matrix you read horizontally.
 *
 * `undefined` when the org has no computed mass, which renders as no bar at all
 * rather than an empty one: unknown strength and zero strength are different
 * claims, and the second is one we would not be entitled to make.
 */
export function cellStrength(comp: DerivedContext, fnId: string): number | undefined {
  const weights = comp.function_weights;
  if (!weights) return undefined;
  const own = weights[fnId];
  if (own === undefined) return undefined;
  let total = 0;
  for (const w of Object.values(weights)) total += w;
  return total > 0 ? own / total : undefined;
}

function FlatGroup({
  comp,
  columns,
  team,
  threshold,
  levelLabel,
  ratingIndex,
  skillFunctions,
  skillFunctionsGrouped,
  cfGate,
  freezeLefts,
  onToggleExpand,
}: RowProps) {
  const { cellScale } = useMatrixCellScale();
  const multiSkill = comp.skills.length > 1;
  return (
    <>
      {comp.skills.map((skill, i) => (
        <Fragment key={skill.id}>
          {/* Column 1 placeholder: the comp name is rendered once on the
              first row of the group. When the comp has >1 skill the cell
              row-spans 2 so a long name can wrap onto a second line
              using the otherwise-empty col-1 slot of the next row. */}
          {i === 1 && multiSkill ? null : (
            <div
              className={`snap-grid__row-label snap-grid__row-label--muted is-frozen${
                i === 0 && multiSkill ? ' snap-grid__row-label--wrap-2' : ''
              }`}
              style={
                i === 0 && multiSkill
                  ? { gridRow: 'span 2', left: freezeLefts[0] }
                  : { left: freezeLefts[0] }
              }
            >
              {i === 0 ? (
                <TruncatedLabel text={comp.display} max={multiSkill ? 60 : 15} />
              ) : null}
            </div>
          )}
          <ConnectorCell
            variant={nestVariant(i, comp.skills.length)}
            onToggle={onToggleExpand}
            frozen
            style={{ left: freezeLefts[1] }}
          />
          <div
            className='snap-grid__row-label snap-grid__row-label--prop is-frozen'
            style={{ left: freezeLefts[2] }}
          >
            <PropIndicator institutional={isInstitutional(skill)} />
          </div>
          <div
            className='snap-grid__row-label is-frozen is-frozen-edge'
            style={{ left: freezeLefts[3] }}
          >
            <TruncatedLabel text={skill.display} />
          </div>
          {columns.map(col => {
            // Shared platform-map gate: no phantom cell where a shared/observed
            // skill would otherwise light a function this derivedContext doesn't serve.
            if (!cfGate.serves(comp.id, col)) {
              return <MatrixCell key={col.fn.id} value={null} levelClass='na' tooltip={null} />;
            }
            const cell = makeFlatCell(
              comp,
              skill,
              col.fn,
              threshold,
              levelLabel,
              ratingIndex,
              mapForColumn(col, skillFunctions, skillFunctionsGrouped),
              team
            );
            return (
              <MatrixCell
                key={col.fn.id}
                {...cell}
                extraClass={cellKindClass(comp, col.fn.id)}
                strength={cellStrength(comp, col.fn.id)}
                scaleByStrength={cellScale}
              />
            );
          })}
        </Fragment>
      ))}
    </>
  );
}

function GroupedRow({
  comp,
  columns,
  team,
  threshold,
  levelLabel,
  ratingIndex,
  skillFunctions,
  skillFunctionsGrouped,
  cfGate,
  freezeLefts,
  ultra = false,
  onToggleExpand,
}: RowProps) {
  const { cellScale } = useMatrixCellScale();
  return (
    <>
      <GroupedCompLabel
        frozen
        freezeLefts={freezeLefts}
        propCount={comp.skills.filter(isInstitutional).length}
        hidePropCount={ultra}
        onToggle={onToggleExpand}
        expanded={false}
        name={<TruncatedLabel text={comp.display} max={22} />}
        count={
          <>
            {comp.skills.length} skill{comp.skills.length === 1 ? '' : 's'}
          </>
        }
      />
      {columns.map(col => {
        // Shared platform-map gate (see FlatGroup) — a shared/observed skill
        // can't manufacture a merged cell under a function this comp doesn't serve.
        if (!cfGate.serves(comp.id, col)) {
          return (
            <MatrixCell
              key={col.fn.id}
              value={null}
              levelClass='na'
              tooltip={null}
              extraClass='is-merged'
            />
          );
        }
        const cell = makeMergedCoverageCell(
          comp,
          col.fn,
          threshold,
          levelLabel,
          ratingIndex,
          mapForColumn(col, skillFunctions, skillFunctionsGrouped),
          team
        );
        return (
          <MatrixCell
            key={col.fn.id}
            {...cell}
            extraClass={['is-merged', cellKindClass(comp, col.fn.id)].filter(Boolean).join(' ')}
            strength={cellStrength(comp, col.fn.id)}
            scaleByStrength={cellScale}
          />
        );
      })}
    </>
  );
}

function makeFlatCell(
  comp: DerivedContext,
  skill: Skill,
  fn: BusinessFunction,
  threshold: RatingValue,
  levelLabel: string,
  ratingIndex: ReturnType<typeof buildRatingIndex>,
  skillFunctions: ReturnType<typeof buildSkillFunctions>,
  team: Engineer[]
) {
  const fns = skillFunctions.get(skill.id);
  if (!fns || !fns.has(fn.id)) {
    return { value: null, levelClass: 'na', tooltip: null };
  }
  const coverage = coverageForCell(
    comp,
    fn,
    threshold,
    ratingIndex,
    skillFunctions,
    team
  );
  const entry = coverage.find(c => c.skill.id === skill.id);
  const value = entry?.covered.length ?? 0;
  const inst = isInstitutional(skill);
  const tooltip: TooltipContent =
    value === 0
      ? {
          title: 'Coverage gap',
          body: `No one rated ${levelLabel} for this skill in this business function.`,
          sub: inst
            ? 'Institutional knowledge — not hireable externally'
            : undefined,
          kind: 'alert',
        }
      : {
          title: `${value} engineer${value === 1 ? '' : 's'} covering`,
          body: entry!.covered
            .map(e => `${e.engineer.display_name} — ${RATING_LABELS[e.rating]}`)
            .join('\n'),
          sub: inst
            ? 'Institutional knowledge — not hireable externally'
            : undefined,
          kind: value === 1 ? 'alert' : 'neutral',
        };
  return { value, levelClass: levelClass(value), tooltip };
}
